import { useContext, useEffect, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import {
  Flex,
  Spinner,
  PaginationProvider,
  ModalContext,
  Box,
  DropdownOptions,
  TooltipPopup,
} from 'dash-ui';

import {
  useGetPortfolioFirstPublicPropertyQuery,
  useManuallyRegisterPortfolioViewMutation,
  useObservePortfoliosChangesSubscription,
  useObservePropertiesChangesSubscription,
} from 'gql/generated.graphql';

import Header from 'flow-components/Header/Header';
import { RoutesPath } from 'flow-components/routes/routes';
import OpenPortfolioWithPasswordModal from 'containers/portfolio/OpenPortfolioWithPasswordModal/OpenPortfolioWithPasswordModal';
import useOpenPortfolioWithPasswordModal from 'containers/portfolio/OpenPortfolioWithPasswordModal/hooks/useOpenPortfolioWithPasswordModal/useOpenPortfolioWithPasswordModal';
import PropertyPreview from 'containers/properties/PropertyPreview/PropertyPreview';
import PortfolioPropertyPreviewSidebar from 'containers/portfolio/PortfolioPropertyPreviewSidebar/PortfolioPropertyPreviewSidebar';
import {
  PropertyPreviewParamsContext,
  PropertyPreviewParamsProvider,
} from 'containers/properties/PropertyPreview/context/PropertyPreviewParamsContext';

const PortfolioPreview = () => {
  const { brokerId = '', portfolioFolderName = '' } = useParams();
  const navigate = useNavigate();
  const { toggleModal } = useContext(ModalContext);
  const { setParams } = useContext(PropertyPreviewParamsContext);
  const [registerView] = useManuallyRegisterPortfolioViewMutation();

  const variables = {
    portfolioFolderName: decodeURIComponent(portfolioFolderName),
    brokerId: decodeURIComponent(brokerId),
  };
  const queryResult = useGetPortfolioFirstPublicPropertyQuery({
    variables,
  });

  const { data, loading, refetch, error } = queryResult;
  const portfolio = useMemo(
    () => data?.getPortfolioFirstPublicProperty,
    [data]
  );
  const property = portfolio?.property;
  // @ts-ignore
  useOpenPortfolioWithPasswordModal(queryResult, variables);
  useObservePropertiesChangesSubscription();
  useObservePortfoliosChangesSubscription({
    onData({ data }) {
      const { document } = data.data?.observePortfoliosChanges || {};
      if (document?._id === portfolio?._id) {
        refetch(variables);
      }
    },
  });

  useEffect(() => {
    if (property === null) {
      navigate(RoutesPath.UnavailablePortfolioPage);
    }
    if (property && portfolio?.portfolioFolder?.showPopup) {
      toggleModal(!error, 'ViewAllPortfolioPropertiesModal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, error, loading]);
  useEffect(() => {
    if (data?.getPortfolioFirstPublicProperty) {
      setParams({
        propertyId: data.getPortfolioFirstPublicProperty.property?._id,
        portfolioFolderId:
          data.getPortfolioFirstPublicProperty._id || undefined,
        brokerId: data.getPortfolioFirstPublicProperty.brokerId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (property && portfolio?.portfolioFolderId)
      registerView({
        variables: { portfolioId: portfolio?.portfolioFolderId },
      });
  }, [property]);

  return (
    <Box w="100%" className='bg-[#F8FBFD]'> {/* bg: theme.colors.b10 */ }
      <Header />
      <DropdownOptions />
      <TooltipPopup />
      <OpenPortfolioWithPasswordModal />
      {loading && (
        <Flex h="100vh" alignItems="center" justifyContent="center" w="100%">
          <Spinner size={32} />
        </Flex>
      )}
      {property && (
        <PropertyPreview
          pageContainerProps={{
            withBreadcrumbs: false,
          }}
          isPortfolioView={true}
          isViewAsClient
          isSaleTeamVisible={false}
          isAddedToMediaKit={!!portfolio.isAddedMediaKit}
          property={property}
          leftSideBar={
            <PaginationProvider>
              <PortfolioPropertyPreviewSidebar
                portfolioId={portfolio._id || ''}
                portfolioFolderId={portfolio.portfolioFolderId || ''}
                withBreadcrumb={false}
                brokerId={portfolio.brokerId}
                currentPropertyId={property._id}
              />
            </PaginationProvider>
          }
        />
      )}
    </Box>
  );
};

const PortfolioPreviewPage = () => (
  <PropertyPreviewParamsProvider>
    <PortfolioPreview />
  </PropertyPreviewParamsProvider>
);
export default PortfolioPreviewPage;
